// src/components/permissions.js

import { useMemo } from "react";

/** Parse env var like: 'a@x.com, b@y.com' */
function parseAgents(raw) {
  return (raw || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

// usage
const agents = parseAgents(process.env.REACT_APP_AGENT_USERS);

/** Get agent emails from environment (build-time). */
export function getAgentEmails() {
  const raw =
    process.env.REACT_APP_AGENT_USERS ||
    // Vite support (optional)
    (typeof import.meta !== "undefined" &&
      import.meta.env &&
      import.meta.env.VITE_AGENT_USERS) ||
    "";

  const envList = parseAgents(raw);

  // Optional: allow a runtime override for debugging (e.g., in console)
  const runtime =
    (typeof window !== "undefined" && window.__AGENT_USERS__) || null;
  const runtimeList = Array.isArray(runtime) ? runtime : null;

  const list = runtimeList || envList;

  // normalize to a Set of lowercase emails
  return new Set(
    list
      .map((e) => String(e).toLowerCase().trim())
      .filter((e) => e.includes("@")),
  );
}

/** Best-effort extraction of an email from various user object shapes. */
export function extractEmail(user) {
  return (
    user?.email ||
    user?.attributes?.email ||
    user?.profile?.email ||
    user?.userProfile?.email ||
    user?.signInUserSession?.idToken?.payload?.email ||
    null
  );
}

/** Pure check: accepts a user object or an email string. */
export function isAgent(userOrEmail, agentSet = getAgentEmails()) {
  const email =
    typeof userOrEmail === "string" ? userOrEmail : extractEmail(userOrEmail);

  if (!email) return false;
  return agentSet.has(email.toLowerCase());
}

/**
 * Hook form: pass your current user and get a stable boolean.
 * Example:
 *   const { user } = useAuthWait();
 *   const amAgent = useIsAgent(user);
 */
export function useIsAgent(user) {
  const agents = useMemo(() => getAgentEmails(), []);
  return useMemo(() => isAgent(user, agents), [user, agents]);
}

/**
 * Component guard: renders children only if user is an agent.
 * Usage:
 *   <AgentOnly user={user} fallback={null}>
 *     <button>Agent Tool</button>
 *   </AgentOnly>
 */
export function AgentOnly({ user, children, fallback = null }) {
  const allowed = useIsAgent(user);
  return allowed ? children : fallback;
}
